import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { isEditHouseholdMemberDatesFlagEnabled } from '../ResidentProfile/hooks';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { promptToaster } from '../App/actions';
import type { GlobalState } from '../App/types';
import ApplicantHousehold from './ApplicantHoushold';
import EmergencyContactsSection from './EmergencyContactsSection';
import InsuranceSection from './InsuranceSection';
import ResidentHousehold from './ResidentHousehold';
import RentableItems from '../../components/ResidentProfileRentableItems';
import {
  Box,
  Button,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import messages from './messages';
import { AddIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
// eslint-disable-next-line max-len
import { Household as HouseholdContainer } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { useModal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import usePermissionsHook from '../../components/ElementWithPermissions/usePermissionsHook';
import PetsSection from './PetsSection';
import Vehicles from './Vehicles';
import WorkOrderPreferences from './WorkOrderPreferences';
import CashPayDetailsSection from './CashPayDetailsSection';
import ChangeHeadOfHouseholdModal from './ChangeHeadOfHousehold';
import {
  getCurrentHeadOfHousehold,
  getEligibleNewHeadOfHouseholdCandidates,
} from './ChangeHeadOfHousehold/eligibility';
import useHouseholdOpenCertsQuery from '../../hooks/data-fetching/useHouseholdOpenCertsQuery';
import useHouseholdWillReopenCertification from '../../hooks/data-fetching/useHouseholdWillReopenCertification';
// eslint-disable-next-line max-len
import buildApplicantHouseholdMembers from '../../hooks/data-fetching/useMiniProfileSubject.helpers';
import * as appSelectors from '../App/selectors';

// Declared once, at module level, so usePermissionsHook's useMemo (keyed on
// this array) isn't re-run on every render.
const RESIDENT_DELETE_SCOPE = ['resident-delete'];

type Props = {
  intl: any,
  actions: any,
  applicationId: string,
  disableEdit: boolean,
  editHouseholdMember: Function,
  onAddHouseholdMember?: Function,
  handleNoVehiclesChange?: Function,
  householdId: string,
  householdMembers: Array<Object>,
  onEdit: Function,
  residentId?: string,
  isOnNTV?: boolean,
  isPriorResident?: boolean,
  hasAssignedUnit?: boolean,
  selectedProperty: Object,
  setHasHouseholdRentableItems: Function,
  refetchHouseholdRentableItemsTrigger: number,
  vehicleCount: number | null,
  unitId?: string,
  onChangeHeadOfHouseholdSuccess?: Function,
  guarantorApplicantTypeId?: ?string,
  liveInCaretakerApplicantTypeId?: ?string,
  relationshipOptions?: Array<Object>,
  affordableRelationshipOptions?: Array<Object>,
  propertyClassType?: string,
  isAffordable?: boolean,
  // Applicant-stage twin of residentId -- application.applicants (Redux,
  // save-ready), passed only by ApplicationProfile. Its mere presence (not
  // residentId) is what turns on the applicant-path Change Head of
  // Household button below; ResidentProfile/PeopleProfile never pass it.
  applicants?: Array<Object>,
  // Whether a household change would reopen a certification here -- only
  // read on the applicant path, see ChangeHeadOfHousehold's own comment.
  // Optional override, mainly for tests; the real signal below is sourced
  // from the household's own affordable-program qualifications query.
  willReopenCertification?: boolean,
  // The household's affordable programs (ApplicationProfile only, from
  // affordableTabs/hudTab) -- drives useHouseholdWillReopenCertification
  // below. ResidentProfile never passes this, which is what keeps that
  // query off on the resident path.
  affordableProgramIds?: Array<string>,
  // Refreshes Redux-backed household data after a card mutation.
  onSaveSuccess?: () => void,
};

const Household = ({
  intl,
  actions,
  actualOrAnticipatedMoveOutDate = null,
  applicationId,
  disableEdit,
  editHouseholdMember,
  onAddHouseholdMember,
  handleNoVehiclesChange,
  householdId,
  hasAssignedUnit,
  isPriorResident,
  unitId,
  householdMembers,
  onEdit,
  residentId,
  selectedProperty,
  setHasHouseholdRentableItems = () => {},
  setHasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate,
  refetchHouseholdRentableItemsTrigger = 0,
  vehicleCount,
  onChangeHeadOfHouseholdSuccess = () => {},
  guarantorApplicantTypeId,
  liveInCaretakerApplicantTypeId,
  relationshipOptions = [],
  affordableRelationshipOptions = [],
  propertyClassType,
  isAffordable: isAffordableProp,
  applicants,
  willReopenCertification: willReopenCertificationProp = false,
  affordableProgramIds = [],
  onSaveSuccess,
}: Props) => {
  const flags = useFlags();
  const editHouseholdPermissions = residentId
    ? ['resident-edit']
    : ['prospect-update'];

  const hasHouseholdEditPermissions = usePermissionsHook({
    scope: editHouseholdPermissions,
  });

  // Deleting a pet removes a household member, so it takes the same
  // permission the Mini Profile requires to delete one.
  const hasResidentDeletePermissions = usePermissionsHook({
    scope: RESIDENT_DELETE_SCOPE,
  });

  // Normalized to the shared helper (ResidentProfile/hooks.js) so this flag
  // check can't drift from the one gating the Add flow's actual handlers
  // (ResidentProfile#addOccupant / #isEditHouseholdMemberDatesEnabled) --
  // same camelCase/kebab-case both-variants check as before, just
  // de-duplicated. No behavior change.
  const isEditHouseholdMemberDatesEnabled =
    isEditHouseholdMemberDatesFlagEnabled(flags);

  // onAddHouseholdMember is an optional prop: ResidentProfile wires it, but
  // ApplicationProfile renders this same component without it (and without a
  // Mini-Profile at all -- it still uses the legacy PERSONAL_INFORMATION_FORM
  // modal). Without this guard the Add button renders there with
  // onClick={undefined}, i.e. a visible button that silently does nothing.
  // Requiring the handler means the button is simply not rendered wherever no
  // Add flow exists yet, and reappears on its own once one is wired up.
  const showAddButton =
    isEditHouseholdMemberDatesEnabled &&
    hasHouseholdEditPermissions &&
    Boolean(onAddHouseholdMember);

  const isApplicantSubject = !residentId && Boolean(applicants);

  // Keep pet rows in member tables when the feature flag is disabled.
  const showPetsSection =
    (Boolean(residentId) || isApplicantSubject) &&
    isEditHouseholdMemberDatesEnabled;

  // Add supersedes Edit, but only where Add actually renders: with the flag
  // off there is no Add, and dropping Edit too would leave the card with no
  // way into the household at all.
  const showEditButton = hasHouseholdEditPermissions && !showAddButton;

  // Mixed properties can carry both Affordable and Conventional households
  // at once (per household/application, not per property) -- ResidentProfile
  // passes the precise household-level classification down as `isAffordable`
  // (the same application.propertyClass signal the backend and the Mini
  // Profile already key off of). Callers that don't have that available yet
  // (ApplicationProfile, PeopleProfile) fall back to the coarser
  // property-level heuristic, which is harmless there since neither renders
  // the Change Head of Household button/modal this value actually feeds.
  const isAffordable =
    typeof isAffordableProp === 'boolean'
      ? isAffordableProp
      : propertyClassType === 'Affordable' || propertyClassType === 'Mixed';

  // Sourced from the same per-program query the Completed tab renders from
  // (see the hook's own comment), not the dead applicationProfile Redux
  // field this used to be threaded from -- gated to the applicant path so
  // the resident path issues no query and keeps its current (always-false)
  // behavior.
  const queryWillReopenCertification = useHouseholdWillReopenCertification({
    organizationId: selectedProperty?.organizationId,
    propertyId: selectedProperty?.id,
    householdId,
    propertyAffordableProgramIds: affordableProgramIds,
    enabled: isApplicantSubject,
  });
  const willReopenCertification =
    willReopenCertificationProp || queryWillReopenCertification;

  // Restore fields that applicant household actions require.
  const headOfHouseholdSourceMembers = residentId
    ? householdMembers
    : buildApplicantHouseholdMembers(applicants);

  const currentHeadOfHousehold = getCurrentHeadOfHousehold(
    headOfHouseholdSourceMembers,
  );
  const eligibleHeadOfHouseholdCandidates =
    getEligibleNewHeadOfHouseholdCandidates(headOfHouseholdSourceMembers, {
      guarantorApplicantTypeId,
      liveInCaretakerApplicantTypeId,
      // A pre-move-in applicant is reshaped with status 'Pending' uniformly
      // -- there is no 'Current' status to require pre-move-in.
      requireCurrentStatus: Boolean(residentId),
    });

  const showChangeHeadOfHouseholdButton =
    isEditHouseholdMemberDatesEnabled &&
    hasHouseholdEditPermissions &&
    (Boolean(residentId) || isApplicantSubject);

  // Same backend restriction changeHeadOfHousehold enforces (and the legacy
  // Edit Household page already gates its own primary-resident radio with) --
  // surfaced here too so the button reflects it instead of only failing
  // after the user has already gone through the modal. householdId is the
  // applicant-stage twin of residentId (a Pending household has no Resident
  // row) -- residentId wins when both are present, so the resident path is
  // unaffected. Also feeds isAddBlockedByOpenCerts below, so it's enabled
  // whenever any consumer that reads it is shown -- the two buttons and the
  // Pets section, which locks its own actions on the same signal. Still
  // fetched (not just consumed) on the applicant path --
  // see isChangeHeadOfHouseholdBlockedByOpenCerts below for why the value
  // itself isn't applied there.
  const { data: householdHasOpenCerts } = useHouseholdOpenCertsQuery({
    organizationId: selectedProperty.organizationId,
    propertyId: selectedProperty.id,
    residentId,
    householdId,
    options: {
      enabled:
        showChangeHeadOfHouseholdButton || showAddButton || showPetsSection,
    },
  });

  // Resident-only: changeHeadOfHousehold (the resident endpoint) refuses an
  // open certification, but the applicant path's updateApplicants has no such
  // check -- applying it there would disable the button for essentially every
  // in-progress application, since the move-in cert is normally open then.
  const isChangeHeadOfHouseholdBlockedByOpenCerts =
    Boolean(residentId) && Boolean(householdHasOpenCerts);

  // Adding a member while a recertification is in progress means the cert
  // completes without them, so the certified household composition no
  // longer matches reality -- same problem Change Head of Household's guard
  // above prevents. Same Boolean(residentId) exemption too: an application
  // always has a MOVE_IN certification open, and adding members pre-move-in
  // is the normal flow.
  const isAddBlockedByOpenCerts =
    Boolean(residentId) && Boolean(householdHasOpenCerts);

  const isChangeHeadOfHouseholdDisabled =
    disableEdit ||
    !currentHeadOfHousehold ||
    eligibleHeadOfHouseholdCandidates.length === 0 ||
    isChangeHeadOfHouseholdBlockedByOpenCerts;

  const {
    open: isChangeHeadOfHouseholdModalOpen,
    openModal: openChangeHeadOfHouseholdModal,
    closeModal: closeChangeHeadOfHouseholdModal,
  } = useModal();

  return (
    <Box padding={2} sx={{ position: 'relative' }}>
      {showAddButton && (
        // Still overlaid rather than passed as the organism's buttonProps:
        // that slot renders the Button itself, leaving nowhere to wrap the
        // Tooltip that explains the open-certification block. Sits where Edit
        // used to, since the two are mutually exclusive.
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <Tooltip
            placement="top"
            title={
              isAddBlockedByOpenCerts
                ? intl.formatMessage(messages.addOpenCertsTooltip)
                : ''
            }
          >
            <Button
              variant="text"
              onClick={onAddHouseholdMember}
              disabled={disableEdit || isAddBlockedByOpenCerts}
              data-testid="add-household-member-button"
            >
              {/* Default size plus the icon gap, matching how the library
                  builds the Add in Vehicles/Emergency Contacts -- this button
                  is hand-rolled for the Tooltip, so the styling isn't
                  inherited. */}
              <AddIcon sx={{ marginRight: 1 }} />
              Add
            </Button>
          </Tooltip>
        </Box>
      )}
      {showChangeHeadOfHouseholdButton && (
        <ChangeHeadOfHouseholdModal
          open={isChangeHeadOfHouseholdModalOpen}
          onClose={closeChangeHeadOfHouseholdModal}
          onSuccess={onChangeHeadOfHouseholdSuccess}
          intl={intl}
          promptToaster={actions.promptToaster}
          currentHeadOfHousehold={currentHeadOfHousehold}
          eligibleCandidates={eligibleHeadOfHouseholdCandidates}
          isAffordable={isAffordable}
          relationshipOptions={relationshipOptions}
          affordableRelationshipOptions={affordableRelationshipOptions}
          organizationId={selectedProperty.organizationId}
          propertyId={selectedProperty.id}
          householdId={householdId}
          isApplicantSubject={isApplicantSubject}
          applicants={applicants}
          applicationId={applicationId}
          willReopenCertification={willReopenCertification}
        />
      )}
      <HouseholdContainer
        intl={intl}
        buttonProps={
          showEditButton
            ? {
                onClick: onEdit,
                disabled: disableEdit,
                buttonVariant: 'edit',
              }
            : // The organism has no "render no button" mode: it always mounts
              // HouseholdButton, and a null/empty buttonProps falls through to
              // its buttonVariant default -- a second, handler-less "Add" that
              // covers the real one. Hidden rather than unmounted so the
              // header keeps the slot our overlay aligns to.
              { style: { visibility: 'hidden' } }
        }
      >
        <>
          {residentId ? (
            <ResidentHousehold
              intl={intl}
              editHouseholdMember={editHouseholdMember}
              householdMembers={householdMembers}
              showChangeHeadOfHouseholdButton={showChangeHeadOfHouseholdButton}
              isChangeHeadOfHouseholdDisabled={isChangeHeadOfHouseholdDisabled}
              hasOpenCertsBlockingChangeHeadOfHousehold={
                isChangeHeadOfHouseholdBlockedByOpenCerts
              }
              onOpenChangeHeadOfHousehold={openChangeHeadOfHouseholdModal}
            />
          ) : (
            <ApplicantHousehold
              intl={intl}
              editHouseholdMember={editHouseholdMember}
              householdMembers={householdMembers}
              showChangeHeadOfHouseholdButton={showChangeHeadOfHouseholdButton}
              isChangeHeadOfHouseholdDisabled={isChangeHeadOfHouseholdDisabled}
              // Always false here: the open-cert restriction is resident-only
              // (see isChangeHeadOfHouseholdBlockedByOpenCerts above), so this
              // tooltip would misdescribe a restriction that isn't real.
              hasOpenCertsBlockingChangeHeadOfHousehold={false}
              onOpenChangeHeadOfHousehold={openChangeHeadOfHouseholdModal}
            />
          )}
          {showPetsSection && (
            <PetsSection
              organizationId={selectedProperty.organizationId}
              propertyId={selectedProperty.id}
              residentId={residentId}
              applicationId={applicationId}
              applicants={applicants}
              canEdit={hasHouseholdEditPermissions && !disableEdit}
              canDelete={hasResidentDeletePermissions && !disableEdit}
              // Apply the certification lock only to resident pets.
              hasOpenCerts={
                Boolean(residentId) && Boolean(householdHasOpenCerts)
              }
              onSaveSuccess={onSaveSuccess}
            />
          )}
          <Vehicles
            intl={intl}
            applicationId={applicationId}
            residentId={residentId}
            handleNoVehiclesChange={handleNoVehiclesChange}
            vehicleCount={vehicleCount}
            selectedProperty={selectedProperty}
            householdId={householdId}
          />
          {flags.rentableItems && (
            <RentableItems
              intl={intl}
              actualOrAnticipatedMoveOutDate={actualOrAnticipatedMoveOutDate}
              hasAssignedUnit={hasAssignedUnit}
              isPriorResident={isPriorResident}
              householdId={householdId}
              promptToaster={actions.promptToaster}
              refetchHouseholdRentableItemsTrigger={
                refetchHouseholdRentableItemsTrigger
              }
              setHasHouseholdRentableItems={setHasHouseholdRentableItems}
              setHasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate={
                setHasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate
              }
            />
          )}
          <EmergencyContactsSection
            intl={intl}
            propertyId={selectedProperty.id}
            organizationId={selectedProperty.organizationId}
            householdId={householdId}
            promptToaster={actions.promptToaster}
          />
          {flags.walkInPayments && (
            <CashPayDetailsSection
              intl={intl}
              householdId={householdId}
              propertyId={selectedProperty.id}
            />
          )}
          {residentId && !isPriorResident && (
            <WorkOrderPreferences
              intl={intl}
              householdId={householdId}
              unitId={unitId}
            />
          )}
          {flags.riskMgmtHouseholdSnapshot &&
            (selectedProperty.insuranceType === 'None' ||
              selectedProperty.insuranceType === 'Asset Protect') &&
            selectedProperty?.config?.allowThirdPartyInsurancePolicies && (
              <InsuranceSection
                intl={intl}
                propertyId={selectedProperty.id}
                organizationId={selectedProperty.organizationId}
                householdId={householdId}
                applicationId={applicationId}
              />
            )}
        </>
      </HouseholdContainer>
    </Box>
  );
};

const mapStateToProps = (state: GlobalState): Object => {
  const { selectedProperty } = state.app;
  const guarantorOptions = appSelectors.getGuarantorOptions(state);
  const liveInCaretakerOptions = appSelectors.getLiveInCaretakerOptions(state);
  return {
    selectedProperty,
    guarantorApplicantTypeId: guarantorOptions?.applicantType?.id,
    liveInCaretakerApplicantTypeId: liveInCaretakerOptions?.applicantType?.id,
    relationshipOptions: appSelectors.getRelationshipOptions(state),
    affordableRelationshipOptions:
      appSelectors.getAffordableRelationshipOptions(state),
    propertyClassType: appSelectors.getSelectedPropertyClassType(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    actions: bindActionCreators(
      {
        promptToaster,
      },
      dispatch,
    ),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(Household));
